'use strict';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import 'rxjs/add/operator/distinctUntilChanged';
const angular = require('angular');
const {copy, equals} = angular;
import {CachedFilter, DEFAULT_PAGE_SIZE} from './CachedFilter';

module.exports = [
  '$log',
  '$filter',
  '$state',
  '$timeout',
  '$persistentCacheFactory',
  BaseFilterMixinFactory];

function BaseFilterMixinFactory($log, $filter, $state, $timeout, $persistentCache) {

  /**
   *
   * @param {$rootScope.Scope} $scope
   * @param {Object} config
   * @param {string} [config.cacheId]
   * @param {string} config.filterTemplate
   * @param {string} config.initialFilter
   * @param {Object} config.filterOptions
   * @param {Function} [config.isDefaultFilterOptions] - override the is default filter options check
   * @param {Object} config.sortOptions
   * @param {string} [config.defaultSortProperty]
   * @param {Object} [config.vm]
   * @param {$rootScope.Scope} [config.filterVm] - the filter may be located in a different scope
   * @param {$rootScope.Scope} [config.filterScope] - the filter may be located in a different scope
   * @param {string} [config.controllerAs]
   * @param {boolean} [config.enablePagination]
   * @param {number} [config.pageSize]
   * @constructor
   */
  function BaseFilterMixin($scope, config) {
    var vm = config.vm ? config.vm : config.controllerAs ? $scope[config.controllerAs] : $scope;
    var filterVm = config.filterVm || vm;
    var filterScope = config.filterScope || $scope;
    var maxCount = config.enablePagination ? (isNumber(config.pageSize) ? config.pageSize : DEFAULT_PAGE_SIZE) : Number.MAX_VALUE;
    var cacheId = config.cacheId;
    var listCache = {};
    var activeSubscriptions = [];
    var activeFilters = [];

    // Persistent Caching System
    const cache = $persistentCache.get('BaseFilterMixinFactory') || $persistentCache('BaseFilterMixinFactory');

    this.activeSubscriptions = activeSubscriptions;
    this.registerFilter = registerFilter;
    this.size = size;
    this.updateRenderLimit = updateRenderLimit;
    this.updateFilter = updateFilter;
    this.updateFilterWithPromise = updateFilterWithPromise;
    this.destroyFiltering = destroyFiltering;

    this.config = Object.assign({}, {
      defaultSortProperty: '$id'
    }, config || {});

    if (!isObject(this.config.sortOptions)) {
      this.config.sortOptions = {
        currentMethod: $filter.SORT_METHODS.ASCENDING.name,
        property: this.config.defaultSortProperty
      }
    }

    // Initialize filter options and retrieve from cache, if available.
    if (!isObject(this.config.filterOptions)) {
      $log.warn(`FilterMixinFactory: created filter with no filterOptions on ${config.controllerAs ? `${config.controllerAs} with ` : ''}scope`, $scope);
      this.config.filterOptions = {};
    }

    var defaultSortOptions = copy(this.config.sortOptions);
    var defaultFilterOptions = copy(this.config.filterOptions);
    var isDefaultFilterOptions = isFunction(this.config.isDefaultFilterOptions)
      ? this.config.isDefaultFilterOptions
      : options => equals(defaultFilterOptions, options);

    this.config.sortOptions = _hydrateCachedOptions(this.config.sortOptions, cacheId ? cache.get(_getOptionsCacheKey('sortOptions', cacheId)) : null);
    this.config.filterOptions = _hydrateCachedOptions(this.config.filterOptions, cacheId ? cache.get(_getOptionsCacheKey('filterOptions', cacheId)) : null);

    if (config.enablePagination) {
      vm.hasMore = hasMore;
      vm.loadMore = loadMore;
      this.loadOne = loadOne;
    }

    filterVm.currentFilter = {
      filter: this.config.initialFilter || '',
      sortOptions: this.config.sortOptions,
      filterOptions: this.config.filterOptions
    };

    filterVm.filterIsDefault = {
      filter: true,
      sortOptions: equals(defaultSortOptions, this.config.sortOptions),
      filterOptions: isDefaultFilterOptions(this.config.filterOptions)
    };

    filterVm.filterModalOptions = {
      template: this.config.filterTemplate,
      locals: {
        defaultFilterOptions: defaultFilterOptions,
        defaultSortOptions: defaultSortOptions,
        filterOptions: filterVm.currentFilter.filterOptions,
        sortOptions: filterVm.currentFilter.sortOptions
      },
      filterOptions: this.config.filterOptions,
      isDefaultFilterOptions: isDefaultFilterOptions
    };

    this.filterOptionsObservable = filterScope.$createObservableFunction('onFilterOptionsUpdate')
      .do(filterOptions => (filterVm.currentFilter.filterOptions = filterOptions, $scope.$evalAsync(noop)))
      .do(_updateFilterOptionsCache)
      .startWith(filterVm.currentFilter.filterOptions);

    this.sortOptionsObservable = filterScope.$createObservableFunction('onSortOptionsUpdate')
      .do(sortOptions => (filterVm.currentFilter.sortOptions = sortOptions, $scope.$evalAsync(noop)))
      .do(_updateSortOptionsCache)
      .startWith(filterVm.currentFilter.sortOptions);

    this.filterObservable = filterScope.$createObservableFunction('filterChanged')
      .startWith(filterVm.currentFilter.filter)
      .debounceTime(200)
      .distinctUntilChanged()
      .do(filter => {
        filterVm.currentFilter.filter = filter;
        $scope.$evalAsync();
      });

    $scope.$on('$destroy', destroyFiltering);

    function registerFilter(filterName) {
      if (config.enablePagination) {
        listCache[filterName] = new CachedFilter(maxCount);
        vm[filterName] = listCache[filterName].rendered;
      } else {
        listCache[filterName] = [];
        vm[filterName] = [];
      }

      activeFilters.push(filterName);
    }

    /**
     * If you provide a deferred, it will resolve the first time the list is non empty
     * @param filterName
     * @param $scope
     * @param [deferred=null]
     * @returns {Function}
     */
    function updateFilterWithPromise(filterName, $scope, deferred) {
      let resolved = false;
      return function (results) {
        if (!resolved) {
          if (results.length > 0) {
            deferred.resolve();
          }
        }
        if (config.enablePagination) {
          $scope.$evalAsync(function () {
            //console.log('updateFilter for ' + filterName + '; len=' + (results ? results.length : 0));
            listCache[filterName].filtered = results;
            listCache[filterName].render($scope);
          });
        } else {
          $scope.$evalAsync(function () {
            //console.log('updateFilter for ' + filterName + '; len=' + (results ? results.length : 0));
            listCache[filterName] = results;
            _replaceArrayContents(vm[filterName], results, filterName, maxCount);
            if ($scope.updateView$) {
              $scope.updateView$.next();
            }
          });
        }
      }
    }

    function updateFilter(filterName, $scope) {
      return function (results) {
        if (config.enablePagination) {
          $scope.$evalAsync(() => {
            //console.log('updateFilter for ' + filterName + '; len=' + (results ? results.length : 0));
            listCache[filterName].filtered = results;
            listCache[filterName].render($scope);
          });
        } else {
          $scope.$evalAsync(() => {
            //console.log('updateFilter for ' + filterName + '; len=' + (results ? results.length : 0));
            listCache[filterName] = results;
            _replaceArrayContents(vm[filterName], results, filterName, maxCount);
            if ($scope.updateView$) {
              $scope.updateView$.next();
            }
          });
        }
      }
    }

    function destroyFiltering() {
      let i = activeSubscriptions.length;

      while (i--) {
        if (isFunction(activeSubscriptions[i].unsubscribe)) {
          activeSubscriptions[i].unsubscribe();
        }
      }

      i = activeFilters.length;

      while (i--) {
        vm[activeFilters[i]] = null;
      }
    }

    // Infinite Scrolling Methods

    // Controller Methods
    function hasMore(filterName) {
      return listCache[filterName] && listCache[filterName].rendered.length < listCache[filterName].filtered.length;
    }

    function loadMore(filterName) {
      //console.log('loading more: ' + filterName);
      updateRenderLimit(filterName);
    }

    function loadOne(filterName, instance) {
      $scope.$evalAsync(function updateRenderedSize() {
        listCache[filterName].addOne($scope, instance);
      });
    }

    // Utility Methods
    function size(filterName) {
      if (listCache[filterName]) {
        if (config.enablePagination) {
          return listCache[filterName].filtered.length;
        } else {
          return listCache[filterName].length;
        }
      } else {
        return 0;
      }
    }

    function updateRenderLimit(filterName, max) {
      //console.log('updateRenderLimit');
      $timeout(function updateRenderedSize() {
        listCache[filterName].addPage();
        listCache[filterName].render($scope);
        $scope.$broadcast('scroll.infiniteScrollComplete');
        //console.log('$broadcast:scroll.infiniteScrollComplete');
        $timeout($scope.$broadcast.bind($scope, 'scroll.infiniteScrollComplete'), 100);
      }, 100);
    }

    function _updateOptions(name) {
      return function _updateOptionsForName(data) {
        filterVm.currentFilter[name] = data;
        return data;
      }
    }

    function _updateFilterOptionsCache(filterOptions) {
      if (cacheId) {
        filterVm.filterIsDefault.filterOptions = equals(defaultFilterOptions, filterOptions);

        if (filterVm.filterIsDefault.filterOptions) {
          cache.remove(_getOptionsCacheKey('filterOptions', cacheId));
        } else {
          cache.put(_getOptionsCacheKey('filterOptions', cacheId), copy(filterOptions));
        }
      }
    }

    function _updateSortOptionsCache(sortOptions) {
      if (cacheId) {
        filterVm.filterIsDefault.sortOptions = equals(defaultSortOptions, sortOptions);

        if (filterVm.filterIsDefault.sortOptions) {
          cache.remove(_getOptionsCacheKey('sortOptions', cacheId));
        } else {
          cache.put(_getOptionsCacheKey('sortOptions', cacheId), copy(sortOptions));
        }
      }
    }

    function _replaceArrayContents(array, source, filterName, maxCount) {
      if (!isArray(array) || (!isArray(source) && !isObject(source))) {
        throw new Error(`replaceArrayContents requires 2 arguments! The first must be an array. The second must be an array or object. Received ${typeof array} and ${ typeof source}`);
      }

      maxCount = isNumber(maxCount) ? maxCount : Number.MAX_VALUE;
      let keys;
      let i = array.length;
      while (i--) {
        array.pop();
      }

      if (isArray(source)) {
        i = Math.min(source.length, maxCount);
        while (i--) {
          array[i] = source[i];
        }
      } else {
        keys = Object.keys(source);
        i = Math.min(keys.length, maxCount);
        while (i--) {
          array[i] = source[keys[i]];
        }
      }
    }
  }

  function _getOptionsCacheKey(name, cacheId) {
    return $state.current.name + (cacheId ? ':' + cacheId : '') + ':' + name;
  }

  function _hydrateCachedOptions(defaultOptions, cachedOptions) {
    if (!cachedOptions) {
      return defaultOptions;
    } else if (cachedOptions.$$hydrated) {
      return cachedOptions;
    } else {
      Object.defineProperty(cachedOptions, '$$hydrated', {value: 'true'});
      return _hydrateOptions(defaultOptions, cachedOptions);
    }
  }

  function _hydrateOptions(liveValue, cachedValue) {
    for (let key in liveValue) {
      if (liveValue.hasOwnProperty(key)) {
        if (isDate(liveValue[key])) {
          if (isString(cachedValue)) {
            cachedValue[key] = new Date(cachedValue[key])
          } else if (!isDate(cachedValue[key])) {
            cachedValue[key] = liveValue[key];
          }

        } else if (isObject(liveValue[key])) {
          if (isObject(cachedValue[key])) {
            cachedValue[key] = _hydrateOptions(liveValue[key], cachedValue[key]);
          } else {
            cachedValue[key] = liveValue[key];
          }
        }
      }
    }
    return cachedValue;
  }

  return BaseFilterMixin;
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/filters/BaseFilterMixinFactory.js